"use client";

import { useEffect, useMemo, useState } from "react";
import cnBind from "classnames/bind";
import { Send } from "lucide-react";
import { useFormik } from "formik";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { createAxiosApi } from "@/shared/axios/axios";
import { Routes } from "@/shared/constants";
import type { OnboardFormValues } from "@/shared/types/onboard.types";
import {
    getRequestTypeByPathname,
    readLeadIntent,
    subscribeLeadIntent,
    type LeadIntent,
} from "@/shared/utils/leadIntent";
import { InputText } from "@/shared/ui/_InputText";
import { Button } from "@/shared/ui/Button";

import styles from "./FormCallBack.module.scss";

const cx = cnBind.bind(styles);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_REGEX = /^[+@]?[\d\s()\-@a-zA-Z_.]{3,40}$/;

export type FormCallBackCopy = {
    eyebrow?: string;
    title?: string;
    description?: string;
    submitLabel?: string;
    successMessage?: string;
    businessPrefix?: string;
    defaultTariff?: string;
};

function buildLeadIntent(pathname: string, stored?: LeadIntent): LeadIntent {
    return {
        ...stored,
        requestType: stored?.requestType || getRequestTypeByPathname(pathname),
        pagePath: stored?.pagePath || pathname,
    };
}

export const FormCallBack = ({
    variant = "default",
    copy,
}: {
    variant?: "default" | "dark";
    copy?: FormCallBackCopy;
}) => {
    const { t } = useTranslation("common");
    const pathname = usePathname() ?? "/";
    const formCopy = useMemo(
        () => ({
            eyebrow: t("home.formFields.eyebrow"),
            title: t("home.formFields.title"),
            description: t("home.formFields.description"),
            submitLabel: t("home.formFields.submitLabel"),
            successMessage: t("home.formFields.successMessage"),
            businessPrefix: t("home.formFields.businessPrefix"),
            defaultTariff: t("home.formFields.defaultTariff"),
            ...copy,
        }),
        [t, copy],
    );
    const [leadIntent, setLeadIntent] = useState<LeadIntent>(() => buildLeadIntent(pathname));
    const [isClientReady, setIsClientReady] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                contactName: Yup.string()
                    .trim()
                    .min(2, t("home.formFields.errShort"))
                    .max(40, t("home.formFields.errLong"))
                    .matches(/^[a-zA-Zа-яА-ЯёЁ\s'-]+$/, t("home.formFields.errName"))
                    .required(t("home.formFields.errRequired")),
                contactPhone: Yup.string()
                    .trim()
                    .matches(CONTACT_REGEX, t("home.formFields.errPhone"))
                    .required(t("home.formFields.errRequired")),
                companyName: Yup.string()
                    .trim()
                    .min(2, t("home.formFields.errShort"))
                    .max(120, t("home.formFields.errLong"))
                    .required(t("home.formFields.errRequired")),
                contactEmail: Yup.string()
                    .trim()
                    .transform((value) => (value === "" ? undefined : value))
                    .optional()
                    .matches(EMAIL_REGEX, t("home.formFields.errEmail")),
            }),
        [t],
    );

    const formik = useFormik<OnboardFormValues>({
        initialValues: {
            companyName: "",
            contactEmail: "",
            contactName: "",
            contactPhone: "",
            _trap: "",
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            if (values._trap) return;

            const body = {
                name: values.contactName,
                email: values.contactEmail.trim() || undefined,
                phone: values.contactPhone,
                business: `${formCopy.businessPrefix}: ${values.companyName}`,
                tariff: leadIntent.tariff || formCopy.defaultTariff,
                requestType: leadIntent.requestType || getRequestTypeByPathname(pathname),
                pagePath: pathname,
            };

            setIsSubmitting(true);
            try {
                await createAxiosApi()({
                    type: "post",
                    url: "/mail",
                    body,
                });
                alert(formCopy.successMessage);
                resetForm();
            } catch (err: unknown) {
                const msg =
                    err && typeof err === "object" && "response" in err
                        ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? "")
                        : "";
                alert(
                    msg
                        ? t("home.formFields.errSubmit", { msg })
                        : t("home.formFields.errNetwork"),
                );
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const isTyping = useMemo(
        () =>
            formik.values.companyName.trim().length > 0 ||
            formik.values.contactEmail.trim().length > 0 ||
            formik.values.contactName.trim().length > 0 ||
            formik.values.contactPhone.trim().length > 0,
        [formik.values],
    );

    useEffect(() => {
        setIsClientReady(true);
        setLeadIntent(buildLeadIntent(pathname, readLeadIntent()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    useEffect(() => {
        if (!isClientReady) return undefined;
        return subscribeLeadIntent((intent) => {
            setLeadIntent(buildLeadIntent(pathname, intent));
        });
    }, [pathname, isClientReady]);

    return (
        <div className={cx("form-callback", "wrapper", variant === "dark" && "theme-dark")}>
            <div className={cx("left-container")}>
                <span className={cx("eyebrow")}>{formCopy.eyebrow}</span>
                <h2 className={cx("title")}>{formCopy.title}</h2>
                <p className={cx("description")}>{formCopy.description}</p>
                <div className={cx("info")}>
                    <div className={cx("item")}>
                        <span>{t("home.formFields.phoneLabel")}</span>
                        <Link href="tel:+375339188819" className={cx("link")}>
                            <span>+375 (33) 918-88-19</span>
                        </Link>
                    </div>

                    <Button
                        onClick={() =>
                            window.open(Routes.TELEGRAM_DIRECT_CONTACT, "_blank", "noopener,noreferrer")
                        }
                        className={cx("button")}
                        label={t("home.formFields.writeTelegram")}
                        variant="outlined"
                    />
                </div>
            </div>

            <form onSubmit={formik.handleSubmit} className={cx("form")}>
                <input type="hidden" name="_trap" value={formik.values._trap} onChange={formik.handleChange} />

                <div
                    className={cx("formPanel", "bg-glass-card", "glass-noise", { formPanelFilled: isTyping })}
                >
                    <div className={cx("fields")}>
                            <InputText
                                placeholder={t("home.formFields.name")}
                                name="contactName"
                                autoComplete="name"
                                value={formik.values.contactName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={cx("glass-input")}
                                error={
                                    formik.touched.contactName && formik.errors.contactName
                                        ? formik.errors.contactName
                                        : ""
                                }
                                rootClassName={cx("form-input", { "input-filled": isTyping })}
                                isFullWidth
                            />
                            <InputText
                                placeholder={t("home.formFields.phone")}
                                name="contactPhone"
                                type="text"
                                autoComplete="tel"
                                value={formik.values.contactPhone}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={cx("glass-input")}
                                error={
                                    formik.touched.contactPhone && formik.errors.contactPhone
                                        ? formik.errors.contactPhone
                                        : ""
                                }
                                rootClassName={cx("form-input", { "input-filled": isTyping })}
                                isFullWidth
                            />
                            <InputText
                                placeholder={t("home.formFields.company")}
                                name="companyName"
                                autoComplete="organization"
                                value={formik.values.companyName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={cx("glass-input")}
                                error={
                                    formik.touched.companyName && formik.errors.companyName
                                        ? formik.errors.companyName
                                        : ""
                                }
                                rootClassName={cx("form-input", { "input-filled": isTyping })}
                                isFullWidth
                            />
                            <InputText
                                placeholder={t("home.formFields.email")}
                                name="contactEmail"
                                type="email"
                                autoComplete="email"
                                value={formik.values.contactEmail}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={cx("glass-input")}
                                error={
                                    formik.touched.contactEmail && formik.errors.contactEmail
                                        ? formik.errors.contactEmail
                                        : ""
                                }
                                rootClassName={cx("form-input", { "input-filled": isTyping })}
                                isFullWidth
                            />

                            <Button
                                type="submit"
                                className={cx("button", "submit")}
                                label={formCopy.submitLabel}
                                variant="solid"
                                iconTrue={false}
                                icon={<Send size={18} strokeWidth={2.2} aria-hidden />}
                                isFullWidth
                                disabled={isSubmitting}
                            />

                            <div className={cx("policy")}>
                                {t("home.formFields.policyBefore")}
                                <Link href={Routes.POLICY}>{t("home.formFields.policyPersonal")}</Link>
                                {t("home.formFields.policyAnd")}
                                <Link href={Routes.TERMS}>{t("home.formFields.policyOffer")}</Link>
                                {t("home.formFields.policyAfter")}
                            </div>
                    </div>
                </div>
            </form>
        </div>
    );
};
